import { useState, useCallback, useEffect, useRef } from 'react'
import AppLayout from './layout/AppLayout'
import Toolbar from './components/Toolbar'
import SideMenu from './components/SideMenu'
import ScreenGenerator from './generator/ScreenGenerator'
import UserMessages from './components/popups/UserMessages'
import { useJsonFetch } from './hooks/useJsonFetch'
import { safeInvoke, formatFrDate, formatFrDateTime } from './lib/utils'
import { LangProvider, translate } from './lib/i18n'

const FONT_SIZES = { sm: '12px', md: '14px', lg: '16px' }

export default function App() {
  const [activeScreen, setActiveScreen] = useState('accueil')
  const [badgeOverrides, setBadgeOverrides] = useState({})
  // `false` par défaut (pas `true`) : `menuData` n'est jamais synchrone au tout premier rendu (même
  // si `window.__TBS_MENU__` est déjà pré-posé par la JSP — useJsonFetch dispatch quand même via un
  // effect, donc après le 1er paint), donc ce 1er paint utilise systématiquement cette valeur par
  // défaut avant la correction ci-dessous (bloc « adjusting state »). `true` provoquait un flash
  // visible du menu (ouvert puis animé fermé) même quand `__TBS_MENU__.state === 'hidden'`.
  const [sidebarOpen, setSidebarOpen]   = useState(false)
  // Désactive la transition CSS de largeur tant qu'aucune ouverture/fermeture explicite (clic
  // hamburger, tbsGlobalMenu.show/hide) n'a eu lieu — la correction automatique du 1er rendu
  // (sidebarOpen: false → valeur réelle de menuData.state) doit être instantanée, pas animée sur
  // 0.25s (on verrait sinon le panneau se déplier visuellement à chaque chargement d'écran).
  const [sidebarAnimated, setSidebarAnimated] = useState(false)
  // Même raison que `sidebarOpen` ci-dessus : `toolbarData` n'est jamais synchrone au 1er rendu,
  // `true` par défaut provoquait un flash visible du bandeau haut même quand
  // `__TBS_TOOLBAR__.state === 'hidden'`.
  const [toolbarVisible, setToolbarVisible] = useState(false)
  const [toolbarButtonOverrides, setToolbarButtonOverrides] = useState({})
  const [darkMode, setDarkMode]       = useState(false)
  const [fontSize, setFontSize]       = useState('md')
  const [lang, setLang]               = useState('fr')
  const [chromeInitialized, setChromeInitialized] = useState(false)
  const userMessagesRef               = useRef(null)

  const { data: menuData }    = useJsonFetch('/mock/menu.json', { windowVar: '__TBS_MENU__' })
  const { data: toolbarData } = useJsonFetch('/mock/toolbar.json', { windowVar: '__TBS_TOOLBAR__' })
  const { data: chatData }    = useJsonFetch('/mock/chat.json', { windowVar: '__TBS_CHAT__' })
  const { data: screenData, loading } = useJsonFetch(`/mock/screen-${activeScreen}.json`, { windowVar: '__TBS_SCREEN__' })

  // Assignation à `undefined` plutôt que `delete` : en production comme dans index.html, ces
  // globales sont pré-déclarées via `var` sur `window`, ce qui les rend non-configurables — un
  // `delete window.xxx` lève une TypeError en mode strict (modules ES). Même contrainte pour
  // tbsGlobalForm (ScreenGenerator.jsx).
  useEffect(() => {
    window.tbsUserMessages = userMessagesRef.current
    return () => { window.tbsUserMessages = undefined }
  }, [])

  useEffect(() => {
    window.tbsGlobalToolbar = {
      hide: () => setToolbarVisible(false),
      show: () => setToolbarVisible(true),
      hideButton: id => setToolbarButtonOverrides(prev => ({ ...prev, [id]: { ...prev[id], hidden: true } })),
      showButton: id => setToolbarButtonOverrides(prev => ({ ...prev, [id]: { ...prev[id], hidden: false } })),
      setButtonBadge: (id, text) => setToolbarButtonOverrides(prev => ({ ...prev, [id]: { ...prev[id], badge: text } })),
      setButtonIcon: (id, css) => setToolbarButtonOverrides(prev => ({ ...prev, [id]: { ...prev[id], icon: css } })),
      setButtonTooltip: (id, tooltipText) => setToolbarButtonOverrides(prev => ({ ...prev, [id]: { ...prev[id], tooltip: tooltipText } })),
    }
    return () => { window.tbsGlobalToolbar = undefined }
  }, [])

  useEffect(() => {
    window.tbsGlobalMenu = {
      hide: () => { setSidebarAnimated(true); setSidebarOpen(false) },
      show: () => { setSidebarAnimated(true); setSidebarOpen(true) },
    }
    return () => { window.tbsGlobalMenu = undefined }
  }, [])

  // Espace utilitaire pour le code métier externe (tbs-business/js) — les helpers de src/lib/utils.js
  // sont des détails internes du bundle React (tree-shakés, non attachés à window), donc invisibles
  // hors des modules ES. Sans état interne (fonctions pures) — cadre d'assignation identique aux
  // autres globales pour rester cohérent, même si aucun effet de bord React n'entre en jeu ici.
  useEffect(() => {
    window.tbsUtils = { formatFrDate, formatFrDateTime }
    return () => { window.tbsUtils = undefined }
  }, [])

  // Etat initial (repli menu, taille police, thème, langue) déduit une seule fois du JSON, dès que
  // les deux sont chargés — reproduit le visu de la session précédente (cf. SPECS-TOOLBAR-MENU.md).
  // Ajustement direct dans le corps du render (pattern React « adjusting state », voir la sync
  // prop value → state de ComboField.jsx) plutôt qu'un effect, pour éviter les re-renders en cascade.
  if (!chromeInitialized && menuData && toolbarData) {
    setChromeInitialized(true)
    setSidebarOpen(menuData.state !== 'hidden')
    setToolbarVisible(toolbarData.state !== 'hidden')
    const langBtn = toolbarData.buttons?.find(b => b.type === 'Languages')
    const selectedLang = langBtn?.languages?.find(l => l[2] && !l[3])
    if (selectedLang) setLang(selectedLang[0])
    const fontBtn = toolbarData.buttons?.find(b => b.type === 'FontSize')
    if (fontBtn?.defaultValue) setFontSize(fontBtn.defaultValue)
    const modeBtn = toolbarData.buttons?.find(b => b.type === 'Mode')
    if (modeBtn?.defaultValue) setDarkMode(modeBtn.defaultValue === 'dark')
  }

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light')
  }, [darkMode])

  useEffect(() => {
    document.documentElement.style.fontSize = FONT_SIZES[fontSize]
  }, [fontSize])

  const handleToggleSidebar = useCallback(() => {
    const next = !sidebarOpen
    setSidebarAnimated(true)
    setSidebarOpen(next)
    safeInvoke(window.onTbsToolbarCallback, 'btnHeadSidebarBtn', next)
  }, [sidebarOpen])

  const handleToolbarButtonEvent = useCallback((id, value, type) => {
    if (type === 'Languages') setLang(value)
    else if (type === 'FontSize') setFontSize(value)
    else if (type === 'Mode') setDarkMode(value === 'dark')
    safeInvoke(window.onTbsToolbarCallback, id, value)
  }, [])

  const handleMenuNavigate = useCallback((id, extraParams) => {
    const handled = safeInvoke(window.onTbsEventCallback, id, 'onOption', extraParams)
    if (handled === false) return
    setActiveScreen(id)
  }, [])

  const handleSetBadge = useCallback((id, text) => {
    setBadgeOverrides(prev => ({ ...prev, [id]: text }))
  }, [])

  const handleTbsEvent = useCallback((id, eventType, ...args) => {
    //console.log('[TBS]', id, eventType, ...args)
    const handled = safeInvoke(window.onTbsEventCallback, id, eventType, ...args)
    // Listeners toujours appelés, même si onTbsEventCallback retourne false
    window.tbsComponents?.[id]?._callListeners?.(eventType, ...args)
    if (handled === false) return
  }, [])

  return (
    <LangProvider value={lang}>
    <AppLayout
      sidebarOpen={sidebarOpen}
      sidebarAnimated={sidebarAnimated}
      toolbar={
        <Toolbar
          toolbarData={toolbarData}
          visible={toolbarVisible}
          buttonOverrides={toolbarButtonOverrides}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={handleToggleSidebar}
          lang={lang}
          fontSize={fontSize}
          darkMode={darkMode}
          onButtonEvent={handleToolbarButtonEvent}
        />
      }
      menu={
        <SideMenu
          items={menuData?.items ?? []}
          activeId={activeScreen}
          onNavigate={handleMenuNavigate}
          badgeOverrides={badgeOverrides}
          onSetBadge={handleSetBadge}
          version={menuData?.version}
        />
      }
      zone={
        loading      ? <p style={{ padding: 16, color: '#5a6270' }}>{translate(lang, 'loading')}</p>
        : screenData ? <ScreenGenerator descriptor={screenData} onEvent={handleTbsEvent} />
        : null
      }
    />
    <UserMessages ref={userMessagesRef} lang={lang} initialChatVisible={chatData ? chatData.state !== 'hidden' : null} />
    </LangProvider>
  )
}
